import React from 'react';
import useComparePins, { MAX_PINS } from '../hooks/useComparePins';

/**
 * CompareButton — a reusable toggle that adds/removes a parcel from
 * the compare basket (#789 / INIT-compare-listings).
 *
 * Lives next to the "Save" action on PropertyCard and inside the
 * ParcelDetail action bar. Reads/writes through useComparePins so it
 * shares the same localStorage-backed source of truth as the global
 * ComparePinBar — clicking here updates the basket pill instantly.
 *
 * Variants:
 *   - `card` (default): small inline button used on listing cards.
 *   - `detail`: larger button to match the ParcelDetail action row.
 *
 * The basket has a hard cap (MAX_PINS = 5). When the cap is hit, the
 * button still works but lights up a warning tone — the underlying
 * hook FIFOs the oldest pin out so the user can keep selecting without
 * an "unpin one first" friction modal.
 */
function CompareButton({ parcelId, variant = 'card', className = '', onToggle }) {
  const { pins, isPinned, toggle } = useComparePins();

  if (!parcelId) return null;

  const inBasket = isPinned(parcelId);
  const atCap = !inBasket && pins.length >= MAX_PINS;

  const handleClick = (event) => {
    // Cards are usually wrapped in a clickable link to the detail page;
    // stop the click from navigating away when the user is just
    // toggling the basket state.
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    toggle(parcelId);
    if (onToggle) onToggle(!inBasket);
  };

  const label = inBasket ? 'In Compare' : 'Compare';
  const ariaLabel = inBasket
    ? `Remove ${parcelId} from compare basket`
    : (atCap
        ? `Add ${parcelId} to compare basket (will replace oldest of ${MAX_PINS})`
        : `Add ${parcelId} to compare basket`);

  // Inline styles keep the component fully self-contained so it can
  // drop into any card / detail context without needing a new CSS
  // module. Pulled the existing PropertyCard pin styling so the
  // visual matches the slice-2 pin toggle the basket pill knows about.
  const isDetail = variant === 'detail';
  const baseStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    border: '1px solid',
    borderRadius: 4,
    cursor: 'pointer',
    fontWeight: 600,
    transition: 'background 0.15s, border-color 0.15s',
  };
  const cardStyle = {
    ...baseStyle,
    fontSize: '0.78rem',
    padding: '3px 8px',
    background: inBasket ? '#e3f2fd' : '#fff',
    borderColor: inBasket ? '#1976d2' : '#ccc',
    color: inBasket ? '#1976d2' : '#444',
  };
  const detailStyle = {
    ...baseStyle,
    fontSize: '0.9rem',
    padding: '8px 14px',
    background: inBasket ? '#e3f2fd' : '#fff',
    borderColor: inBasket ? '#1976d2' : '#1976d2',
    color: '#1976d2',
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      aria-label={ariaLabel}
      aria-pressed={inBasket}
      title={atCap ? `Basket is full (${MAX_PINS}) — adding will drop the oldest pin` : ariaLabel}
      data-parcel-id={parcelId}
      data-in-basket={inBasket ? 'true' : 'false'}
      className={`compare-button compare-button--${variant} ${className}`.trim()}
      style={isDetail ? detailStyle : cardStyle}
    >
      <span aria-hidden="true" style={{ fontSize: isDetail ? '1rem' : '0.85rem' }}>
        {inBasket ? '✓' : '+'}
      </span>
      <span>{label}</span>
    </button>
  );
}

export default CompareButton;
